// =============================================================================
// features/admin/feature-flags/UserAxisSelect.tsx — Eixo "usuários específicos"
// da audience de uma flag (F32-S04).
//
// Regras (doc 26 §21, notas do slot):
//   - Grava sempre UUID. Busca é só para achar a pessoa — nome/e-mail nunca
//     é enviado ao backend.
//   - NUNCA lista o diretório inteiro por padrão: só consulta
//     GET /api/admin/users?search=... a partir de 2 caracteres digitados
//     (useUsers com `enabled` condicional — F32-S04 estende useUsers).
//   - Nomes resolvidos ficam em cache local (nameCache) só para os usuários
//     que passaram pela busca NESTA sessão de edição. UUIDs que já vinham
//     salvos na flag (adicionados por outro admin/via API) aparecem como
//     UUID truncado + aviso — resolver o nome exigiria expor o diretório
//     inteiro, o que a tela explicitamente não faz.
// =============================================================================

import * as React from 'react';

import { useUsers } from '../../../hooks/admin/useUsers';
import { cn } from '../../../lib/cn';

import { RemovableChip } from './RemovableChip';
import { UserSearchResults } from './UserSearchResults';

interface UserAxisSelectProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
  /** Nomes já conhecidos de sessões anteriores (ex: carregado do form pai). */
  knownNames?: Record<string, string>;
}

const MIN_QUERY_LENGTH = 2;

export function UserAxisSelect({
  value,
  onChange,
  disabled = false,
  knownNames = {},
}: UserAxisSelectProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [nameCache, setNameCache] = React.useState<Record<string, string>>(knownNames);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleQueryChange(v: string): void {
    setQuery(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(v), 300);
  }

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const searchReady = debounced.trim().length >= MIN_QUERY_LENGTH;

  const { data, isLoading } = useUsers(
    { search: debounced.trim(), active: 'true', limit: 10 },
    { enabled: searchReady },
  );

  const results = React.useMemo(
    () => (data?.data ?? []).filter((u) => !value.includes(u.id)),
    [data, value],
  );

  // Cacheia nomes resolvidos localmente (chips ficam legíveis nesta sessão).
  React.useEffect(() => {
    if (!data?.data.length) return;
    setNameCache((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const u of data.data) {
        if (next[u.id] !== u.fullName) {
          next[u.id] = u.fullName;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data]);

  function add(id: string, fullName: string): void {
    onChange([...value, id]);
    setNameCache((prev) => ({ ...prev, [id]: fullName }));
    setQuery('');
    setDebounced('');
    setOpen(false);
  }

  function remove(id: string): void {
    onChange(value.filter((v) => v !== id));
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const name = nameCache[id];
            const chipLabel = name ?? (
              <code className="font-mono normal-case tracking-normal">{id.slice(0, 8)}…</code>
            );
            return disabled ? (
              <span
                key={id}
                className="inline-flex items-center rounded-pill px-2.5 py-0.5 font-sans text-xs"
                style={{ background: 'var(--surface-muted)', color: 'var(--text-3)' }}
              >
                {chipLabel}
              </span>
            ) : (
              <RemovableChip
                key={id}
                ariaLabel={`Remover usuário ${name ?? id}`}
                onRemove={() => remove(id)}
              >
                {chipLabel}
              </RemovableChip>
            );
          })}
        </div>
      )}

      {value.some((id) => !nameCache[id]) && (
        <p className="font-sans text-xs text-ink-4">
          UUIDs sem nome resolvido vieram de uma configuração anterior — busque o nome abaixo para
          confirmar quem é antes de confiar no piloto.
        </p>
      )}

      {!disabled && (
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder="Buscar usuário por nome ou e-mail…"
            className={cn(
              'w-full px-3 py-2 rounded-sm text-sm',
              'border border-border-strong font-sans text-ink',
              'placeholder:text-ink-4',
              'focus:outline-none focus:border-azul',
              'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15)]',
              'transition-[border-color,box-shadow] duration-fast ease',
            )}
            style={{
              background: 'var(--bg-elev-1)',
              boxShadow: 'inset 0 1px 2px var(--border-inner-dark)',
            }}
          />

          {open && query.trim().length > 0 && (
            <UserSearchResults
              searchReady={searchReady}
              isLoading={isLoading}
              results={results}
              query={debounced}
              minQueryLength={MIN_QUERY_LENGTH}
              onSelect={add}
            />
          )}
        </div>
      )}
    </div>
  );
}
